import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Spacing } from '../../ui';
import { useApi } from '../../hooks';
import { saveErrorText } from './saveLossChanges';
import {
  buildAdoptedPayload,
  candidateMatchesQuery,
  candidateOptionText,
  describeCandidate,
  mostRecentlyBoundApplicationId,
  selfReportedRows,
} from './replaceLossHistory';
import { Button as ShadButton } from '../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList } from '../../components/ui/combobox';

// Clear all / Adopt from… count losses in their modal and notice text at three call sites each.
const pluralizeLoss = (count) => (count === 1 ? 'loss' : 'losses');

// What the picker's input shows once a candidate is chosen — the same `<application> · <term>` the
// modal and notice text use.
const candidateInputLabel = (candidate) => (candidate ? describeCandidate(candidate).label : '');

/* Clear all and Adopt from… (UND-987): the two operations that replace an application's complete
   self-reported loss run in one PUT, outside LossManagement's per-row staging session. Renders its
   own trigger buttons (meant to sit inline in the toolbar) and its own two confirmation modals, and
   owns every bit of state and every fetch Adopt's picker needs — LossManagement only supplies what it
   can't know on its own (the application, whether per-row edits are staged) and gets told when a
   replace has landed, via `onReplaced`, so it can exitEditing, refresh
   and show its own notice the same way `onPressSave` does. */
const ReplaceLossHistoryDialogs = ({ application, hasUnsavedEdits, disabled, onBusyChange, onReplaced }) => {
  const api = useApi();

  const [replaceAction, setReplaceAction] = useState(null);
  const [adoptSelectedId, setAdoptSelectedId] = useState(null);
  // The customer's other applications, fetched each time Adopt from… opens: null until they arrive.
  const [candidateApplications, setCandidateApplications] = useState(null);
  const [candidatesError, setCandidatesError] = useState(null);
  const candidatesRequest = useRef(0);
  // Set only on a successful fetch, and only ever holding the rows for `applicationId` — the id it
  // was loaded for. Cleared the moment the selection changes, so a fetch that fails (or one still in
  // flight) can never leave a previous source's rows sitting around to be adopted under a new label.
  const [adoptSource, setAdoptSource] = useState(null);
  const [adoptSourceLoading, setAdoptSourceLoading] = useState(false);
  const [replaceError, setReplaceError] = useState(null);
  const [replaceBusy, setReplaceBusy] = useState(false);

  // Reported upward so LossManagement's own busy state (which gates Add Loss, Save, Edit Losses…)
  // stays true for the moment a replace PUT is actually in flight, exactly as it was before this
  // was its own component.
  useEffect(() => {
    if (onBusyChange) {
      onBusyChange(replaceBusy);
    }
  }, [replaceBusy, onBusyChange]);

  // What Clear all and Adopt from… actually replace: the application's complete self-reported set,
  // suppressed rows included — not just what the grid currently shows.
  const selfReportedCount = useMemo(() => selfReportedRows(application.lossHistory).length, [application]);

  const closeReplaceModal = useCallback(() => {
    candidatesRequest.current += 1;
    setReplaceAction(null);
    setReplaceError(null);
    setAdoptSource(null);
    setAdoptSourceLoading(false);
    setCandidateApplications(null);
    setCandidatesError(null);
  }, []);

  const openClearAllModal = useCallback(() => {
    setReplaceError(null);
    setReplaceAction('clear');
  }, []);

  const openAdoptModal = useCallback(() => {
    const request = ++candidatesRequest.current;
    setReplaceError(null);
    setAdoptSource(null);
    setAdoptSelectedId(null);
    setCandidateApplications(null);
    setCandidatesError(null);
    setReplaceAction('adopt');
    api
      .getApplicationsForCustomer(application.customerId)
      .then((applications) => {
        if (request !== candidatesRequest.current) return;
        const others = (applications || []).filter((candidate) => candidate.id !== application.id);
        setCandidateApplications(others);
        // Left unselected when nothing has ever bound — Confirm stays disabled until the underwriter
        // picks one themselves, rather than guessing which unbound application they meant.
        setAdoptSelectedId(mostRecentlyBoundApplicationId(others));
      })
      .catch((error) => {
        if (request !== candidatesRequest.current) return;
        setCandidatesError(saveErrorText(error, "Could not load this customer's applications"));
      });
  }, [api, application.customerId, application.id]);

  // Fetches the picked application's own loss history — the count the modal shows and the rows
  // Confirm sends both come from here, so there is exactly one read of it per selection. Fresh, not
  // cached: the source may have been edited moments ago in another tab, and Confirm copies exactly
  // what this read returns.
  useEffect(() => {
    if (replaceAction !== 'adopt') {
      return undefined;
    }
    // The picker was cleared: nothing is selected, so nothing is loading, and a previous
    // selection's rows or load error no longer describe anything on offer.
    if (!adoptSelectedId) {
      setAdoptSource(null);
      setAdoptSourceLoading(false);
      setReplaceError(null);
      return undefined;
    }
    let cancelled = false;
    // Clear whatever a previous selection loaded (successfully or not) before this selection's own
    // fetch starts — Confirm must never stay enabled on a stale source while a new one is loading.
    setAdoptSource(null);
    setAdoptSourceLoading(true);
    setReplaceError(null);
    api
      .getApplicationWithId(adoptSelectedId, true, { fresh: true })
      .then((sourceApplication) => {
        if (!cancelled) {
          setAdoptSource({ applicationId: adoptSelectedId, lossHistory: sourceApplication.lossHistory || [] });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setReplaceError(saveErrorText(error, "Could not load that application's losses"));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setAdoptSourceLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [replaceAction, adoptSelectedId, api]);

  // The only condition under which Adopt's Confirm may run: the currently selected application's
  // losses loaded successfully (its id matches what was loaded, nothing is in flight, and nothing
  // failed — a failed or in-flight load leaves `adoptSource` null, so this is false either way).
  const adoptSourceReady = adoptSource !== null && adoptSource.applicationId === adoptSelectedId && !adoptSourceLoading;

  const adoptSourceCount = useMemo(() => (adoptSourceReady ? selfReportedRows(adoptSource.lossHistory).length : 0), [adoptSourceReady, adoptSource]);
  const adoptSelectedApplication = useMemo(
    () => (candidateApplications || []).find((candidate) => candidate.id === adoptSelectedId) || null,
    [candidateApplications, adoptSelectedId]
  );
  const adoptSourceLabel = candidateInputLabel(adoptSelectedApplication);

  const onConfirmReplace = useCallback(async () => {
    if (replaceBusy || (replaceAction === 'adopt' && !adoptSourceReady)) {
      return;
    }
    const payload = replaceAction === 'clear' ? [] : buildAdoptedPayload(adoptSource.lossHistory, application);
    const notice =
      replaceAction === 'clear'
        ? `Cleared ${selfReportedCount} ${pluralizeLoss(selfReportedCount)} from this application`
        : `Replaced ${selfReportedCount} ${pluralizeLoss(selfReportedCount)} with ${payload.length} from ${adoptSourceLabel}`;

    setReplaceBusy(true);
    setReplaceError(null);
    try {
      await api.putApplicationLossHistory(application.id, payload);
      // LossManagement's job from here: exitEditing, refresh, show the notice — the same sequence
      // onPressSave runs after a successful save.
      await onReplaced(notice);
      closeReplaceModal();
    } catch (error) {
      // Grid unchanged: nothing local was mutated above, only this modal's own error text.
      setReplaceError(saveErrorText(error, 'Could not replace loss history'));
    } finally {
      setReplaceBusy(false);
    }
  }, [
    replaceBusy,
    replaceAction,
    adoptSourceReady,
    adoptSource,
    application,
    selfReportedCount,
    adoptSourceLabel,
    api,
    onReplaced,
    closeReplaceModal,
  ]);

  return (
    <>
      <Spacing horizontal="1" />
      <ShadButton variant="outline" size="sm" onClick={openClearAllModal} disabled={disabled}>
        Clear all
      </ShadButton>
      {application.customerId ? (
        <>
          <Spacing horizontal="1" />
          <ShadButton variant="outline" size="sm" onClick={openAdoptModal} disabled={disabled}>
            Adopt from…
          </ShadButton>
        </>
      ) : null}
      <Dialog open={replaceAction === 'clear'} onOpenChange={(open) => !open && closeReplaceModal()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clear all losses?</DialogTitle>
            <DialogDescription>
              Clear all {selfReportedCount} {pluralizeLoss(selfReportedCount)} on this application? Skyward claims are not affected.
              {hasUnsavedEdits ? ' Your unsaved edits to this grid will be discarded.' : ''}
            </DialogDescription>
          </DialogHeader>
          {replaceError ? <p className="text-sm text-destructive">{replaceError}</p> : null}
          <DialogFooter>
            <ShadButton variant="outline" onClick={closeReplaceModal} disabled={replaceBusy}>
              Cancel
            </ShadButton>
            <ShadButton onClick={onConfirmReplace} disabled={replaceBusy}>
              {replaceBusy ? 'Clearing…' : 'Clear all losses'}
            </ShadButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={replaceAction === 'adopt'} onOpenChange={(open) => !open && closeReplaceModal()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adopt from…</DialogTitle>
            <DialogDescription>Replace this application's loss history with a copy of one of this customer's other applications.</DialogDescription>
          </DialogHeader>
          {candidatesError ? (
            <p className="text-sm text-destructive">{candidatesError}</p>
          ) : candidateApplications === null ? (
            <p className="text-sm text-muted-foreground">Loading applications…</p>
          ) : candidateApplications.length === 0 ? (
            <p className="text-sm text-muted-foreground">This customer has no other applications.</p>
          ) : (
            <Combobox
              items={candidateApplications}
              value={adoptSelectedApplication}
              onValueChange={(next) => setAdoptSelectedId(next ? next.id : null)}
              itemToStringLabel={candidateInputLabel}
              isItemEqualToValue={(candidate, selected) => !!candidate && !!selected && candidate.id === selected.id}
              filter={candidateMatchesQuery}
              autoHighlight
            >
              <ComboboxInput aria-label="Source application" placeholder="Search applications…" className="w-full" />
              <ComboboxContent>
                <ComboboxList>
                  {(candidate) => (
                    <ComboboxItem key={candidate.id} value={candidate}>
                      {candidateOptionText(candidate)}
                    </ComboboxItem>
                  )}
                </ComboboxList>
                <ComboboxEmpty>No matching applications</ComboboxEmpty>
              </ComboboxContent>
            </Combobox>
          )}
          {adoptSourceLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : adoptSourceReady ? (
            <p className="text-sm">
              {`Replace all ${selfReportedCount} ${pluralizeLoss(
                selfReportedCount
              )} on this application with ${adoptSourceCount} from ${adoptSourceLabel}? Skyward claims are not affected.`}
              {hasUnsavedEdits ? ' Your unsaved edits to this grid will be discarded.' : ''}
            </p>
          ) : null}
          {replaceError ? <p className="text-sm text-destructive">{replaceError}</p> : null}
          <DialogFooter>
            <ShadButton variant="outline" onClick={closeReplaceModal} disabled={replaceBusy}>
              Cancel
            </ShadButton>
            <ShadButton onClick={onConfirmReplace} disabled={replaceBusy || !adoptSourceReady}>
              {replaceBusy ? 'Replacing…' : 'Replace'}
            </ShadButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export { ReplaceLossHistoryDialogs };
